/**
 * src/components/spatial/spatialSemantics.ts
 *
 * Centralised visual-semantic system for GeoTrust map layers.
 *
 * Every deck.gl (and, over time, native) layer must derive its colours
 * from these tokens instead of hard-coding hex values in each
 * component. This keeps the map legible and consistent, and gives us a
 * single place to retheme.
 *
 * Colours are expressed as deck.gl RGBA tuples ([r,g,b] or [r,g,b,a],
 * 0-255). The base hues intentionally match the fallback fill colours
 * that the legacy native layers used in `spatialLayerUtils.ts`
 * (building `#6366f1`, floor `#3b82f6`, zone `#22c55e`, slice `#ef4444`)
 * so the deck path looks like the app operators already know.
 *
 * NOTE: this module owns semantic MEANING, not final brand values. When
 * GeoTrust adopts a formal design-token source, swap the hexes below
 * for theme lookups — callers won't change.
 */

import type { SpatialObjectFeature } from '../../services/spatial/geoLinkTypes';
import type { PolicyState } from '../../services/spatial/map/mapTypes';

export type RGBA = [number, number, number, number];
export type RGB = [number, number, number];

/** The canonical semantic states a spatial entity can present. */
export type SpatialStatus =
  | 'default'
  | 'verified'
  | 'pending'
  | 'restricted'
  | 'inactive'
  | 'active'
  | 'ruleTriggered'
  | 'live'
  | 'warning'
  | 'selected'
  | 'hovered';

/** Parse `#rrggbb` (or `#rgb`) into an RGB tuple. Falls back to grey. */
export function hexToRgb(hex: string): RGB {
  const fallback: RGB = [148, 163, 184]; // slate-400
  if (typeof hex !== 'string') return fallback;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return fallback;
  const n = Number.parseInt(h, 16);
  if (!Number.isFinite(n)) return fallback;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Semantic base colours (RGB). */
export const SEMANTIC_RGB: Record<SpatialStatus, RGB> = {
  default:       hexToRgb('#6366f1'), // indigo-500 (matches legacy building)
  verified:      hexToRgb('#22c55e'), // green-500
  pending:       hexToRgb('#f59e0b'), // amber-500
  restricted:    hexToRgb('#ef4444'), // red-500
  inactive:      hexToRgb('#6b7280'), // gray-500
  active:        hexToRgb('#3b82f6'), // blue-500
  ruleTriggered: hexToRgb('#a855f7'), // purple-500
  live:          hexToRgb('#ec4899'), // pink-500
  warning:       hexToRgb('#f97316'), // orange-500
  selected:      hexToRgb('#facc15'), // yellow-400
  hovered:       hexToRgb('#38bdf8'), // sky-400
};

/** Per-object-type base fill hue, matching the legacy native layers. */
const OBJECT_TYPE_RGB: Record<string, RGB> = {
  building: hexToRgb('#6366f1'),
  floor:    hexToRgb('#3b82f6'),
  zone:     hexToRgb('#22c55e'),
  slice:    hexToRgb('#ef4444'),
};

function withAlpha(rgb: RGB, alpha: number): RGBA {
  return [rgb[0], rgb[1], rgb[2], Math.max(0, Math.min(255, Math.round(alpha)))];
}

type Props = Record<string, unknown>;

function readMeta(props: Props): Props {
  const m = props.metadata;
  return m && typeof m === 'object' ? (m as Props) : {};
}

/**
 * Derive the primary semantic status for a spatial-object feature from
 * the properties GeoLink returns on the /render envelope. Best-effort:
 * GeoLink's public render features are intentionally sparse, so we read
 * whatever authoritative flags are present and fall back to `default`.
 *
 * Precedence (most-important wins): restricted > ruleTriggered >
 * pending > active(rule-linked) > verified > inactive > default.
 */
export function deriveSpatialStatus(feature: SpatialObjectFeature): SpatialStatus {
  const props = (feature?.properties ?? {}) as Props;
  const meta = readMeta(props);

  const isRestricted =
    props.visibility === 'restricted' ||
    props.restricted === true ||
    meta.restricted === true;
  if (isRestricted) return 'restricted';

  if (meta.ruleTriggered === true || props.ruleTriggered === true) return 'ruleTriggered';

  if (meta.pendingReview === true || props.pendingReview === true || props.status === 'pending') {
    return 'pending';
  }

  const hasRule =
    props.linkHasExecutionRule === true ||
    (props.executionRuleId != null && props.executionRuleId !== '');
  if (hasRule) return 'active';

  if (props.verified === true || meta.verified === true) return 'verified';

  if (props.is_active === false || props.isActive === false || props.status === 'inactive') {
    return 'inactive';
  }

  return 'default';
}

export interface SelectionState {
  selectedId?: string | null;
  hoveredId?: string | null;
}

/** Stable id for a spatial-object feature (mirrors the map-layer keying). */
export function spatialFeatureId(feature: SpatialObjectFeature): string {
  if (feature?.id != null) return String(feature.id);
  const p = (feature?.properties ?? {}) as Props;
  for (const k of ['objectId', 'object_id', 'spatial_object_id', 'spatialObjectId', 'id']) {
    const v = p[k];
    if (typeof v === 'string' && v) return v;
    if (typeof v === 'number') return String(v);
  }
  return '';
}

/**
 * Fill colour for a spatial-object feature, taking selection/hover into
 * account. Selected wins over hovered wins over the derived status.
 * Uses the object-type hue for `default`/`active`/`verified` so
 * buildings/floors/zones/slices keep their familiar palette, and the
 * semantic hue for exceptional states (restricted, ruleTriggered, …).
 */
export function getSpatialFillColor(feature: SpatialObjectFeature, sel: SelectionState = {}): RGBA {
  const id = spatialFeatureId(feature);
  if (sel.selectedId && id && id === sel.selectedId) return withAlpha(SEMANTIC_RGB.selected, 235);
  if (sel.hoveredId && id && id === sel.hoveredId) return withAlpha(SEMANTIC_RGB.hovered, 220);

  const status = deriveSpatialStatus(feature);

  // Exceptional statuses use the semantic hue directly.
  if (status === 'restricted' || status === 'ruleTriggered' || status === 'pending' ||
      status === 'warning' || status === 'live' || status === 'inactive') {
    return withAlpha(SEMANTIC_RGB[status], 210);
  }

  // Normal objects keep their per-type hue; rule-linked ("active") gets
  // a slightly stronger alpha so operators can spot policy-linked ones.
  const props = (feature?.properties ?? {}) as Props;
  const objType = typeof props.objectType === 'string' ? props.objectType : 'building';
  const base = OBJECT_TYPE_RGB[objType] ?? SEMANTIC_RGB.default;
  return withAlpha(base, status === 'active' ? 220 : 190);
}

/** Line/outline colour — selected/hovered echo the fill; otherwise a dim contrast. */
export function getSpatialLineColor(feature: SpatialObjectFeature, sel: SelectionState = {}): RGBA {
  const id = spatialFeatureId(feature);
  if (sel.selectedId && id && id === sel.selectedId) return withAlpha(SEMANTIC_RGB.selected, 255);
  if (sel.hoveredId && id && id === sel.hoveredId) return withAlpha(SEMANTIC_RGB.hovered, 255);
  const status = deriveSpatialStatus(feature);
  if (status === 'restricted') return withAlpha(SEMANTIC_RGB.restricted, 255);
  return [255, 255, 255, 120];
}

/**
 * Elevation (extrusion height, metres) for a feature — mirrors the
 * legacy `buildExtrusionHeightExpr` coalesce chain
 * (`heightMeters` → `height` → 1) so the deck path matches the native
 * fill-extrusion look.
 */
export function getSpatialElevation(feature: SpatialObjectFeature): number {
  const p = (feature?.properties ?? {}) as Props;
  const h = p.heightMeters ?? p.height ?? 1;
  const n = Number(h);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/** Base height (metres) for a feature (`baseHeightMeters` → `base_height` → 0). */
export function getSpatialBaseHeight(feature: SpatialObjectFeature): number {
  const p = (feature?.properties ?? {}) as Props;
  const b = p.baseHeightMeters ?? p.base_height ?? 0;
  const n = Number(b);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/**
 * Country-policy normalized-state palette (choropleth). Consume
 * GeoLink's `effectiveState` directly — never reinterpret raw contract
 * fields. Green = permissive, amber = conditional, red = blocked, grey
 * = unknown/not applicable.
 */
export const POLICY_STATE_RGB: Record<PolicyState, RGB> = {
  allowed:               hexToRgb('#22c55e'), // green-500
  permitRequired:        hexToRgb('#f59e0b'), // amber-500
  verificationRequired:  hexToRgb('#eab308'), // yellow-500
  adminApprovalRequired: hexToRgb('#f97316'), // orange-500
  restricted:            hexToRgb('#fb7185'), // rose-400
  denied:                hexToRgb('#ef4444'), // red-500
  unsupported:           hexToRgb('#6b7280'), // gray-500
  notConfigured:         hexToRgb('#94a3b8'), // slate-400
};

/** Choropleth fill for a country-policy state (defaults to notConfigured). */
export function getPolicyStateColor(state: PolicyState | string | null | undefined, alpha = 150): RGBA {
  const rgb = (state && (POLICY_STATE_RGB as Record<string, RGB>)[state]) || POLICY_STATE_RGB.notConfigured;
  return [rgb[0], rgb[1], rgb[2], Math.max(0, Math.min(255, Math.round(alpha)))];
}
